import {
  __ONLY_WRITE_STATE_FROM_ACTIONS as writeState,
  readState,
} from "@/__main__/app-state.mjs";
import { isPersistent } from "@/__main__/constants.mjs";
import { postData } from "@/__main__/get-data.mjs";
import { setVolatileKV, writeSettings } from "@/app/actions.mjs";
import { initAuth } from "@/feature-auth/utils/auth-actions.mjs";
import getBearerToken from "@/feature-auth/utils/get-auth-request-header.mjs";
import { createBraintreeClientToken } from "@/feature-wallet/api.mjs";
import { CreateBraintreeClientTokenModel } from "@/feature-wallet/models/create-braintree-token.mjs";
import type { PaymentMethod } from "@/feature-wallet/models/payment-method.mjs";
import type { UserSubscription } from "@/feature-wallet/models/user-subscriptions.mjs";
import type { UserTransaction } from "@/feature-wallet/models/user-transactions.mjs";
import { getPaymentMethod } from "@/feature-wallet/utils/payment-methods.mjs";
import { getPaymentProvider } from "@/feature-wallet/utils/payment-provider.mjs";
import { isTrialSubscription } from "@/feature-wallet/utils/subscription-status.mjs";
import addInitialPath from "@/util/add-initial-path.mjs";
import clone from "@/util/clone.mjs";
import { devDebug, devError } from "@/util/dev.mjs";

export function initWalletState() {
  addInitialPath(["wallet"], {
    transactions: [],
    paymentProviders: [],
    subscriptions: [],
    paymentPlans: {},
  });
}

export function removeWalletState() {
  for (const key in readState.wallet) {
    delete writeState.wallet[key];
  }
  delete writeState.wallet;

  setVolatileKV("braintreeClientToken", null);
}

export function addPaymentMethod(newMethod: PaymentMethod) {
  const paymentProviders = clone(readState.wallet.paymentProviders);

  let paymentProvider = getPaymentProvider({
    paymentProviders,
    providerType: newMethod.providerType,
  });

  if (paymentProvider === null) {
    paymentProvider = {
      defaultPaymentMethodId: newMethod.id,
      providerType: newMethod.providerType,
      paymentMethods: [],
    };

    paymentProviders.push(paymentProvider);
  }

  const methodAlreadyExists = paymentProvider.paymentMethods.some(
    ({ id }) => id === newMethod.id,
  );

  if (!methodAlreadyExists) {
    // @ts-expect-error - We know that `paymentProvider` and `newMethod` have the same `providerType`.
    paymentProvider.paymentMethods.unshift(newMethod);
  }

  paymentProviders[isPersistent] = true;
  writeState.wallet.paymentProviders = paymentProviders;

  if (!readState.settings.wallet.defaultPaymentMethodId) {
    updateDefaultPaymentMethod(newMethod.id);
  }
}

export function removePaymentMethod(paymentMethodId: string) {
  const paymentProviders = clone(readState.wallet.paymentProviders);

  for (const paymentProvider of paymentProviders) {
    const removeIdx = paymentProvider.paymentMethods.findIndex(
      ({ id }) => id === paymentMethodId,
    );

    if (removeIdx === -1) {
      continue;
    }

    paymentProvider.paymentMethods.splice(removeIdx, 1);
  }

  paymentProviders[isPersistent] = true;
  writeState.wallet.paymentProviders = paymentProviders;

  const relatedSub = readState.wallet.subscriptions.find(
    ({ paymentMethodId: sId }) => sId === paymentMethodId,
  );

  if (relatedSub) {
    updateSubscription({ ...relatedSub, paymentMethodId: null });
  }

  if (readState.settings.wallet.defaultPaymentMethodId === paymentMethodId) {
    updateDefaultPaymentMethod(null);
  }
}

export function updateDefaultPaymentMethod(paymentMethodId: string | null) {
  writeSettings(["wallet"], {
    ...readState.settings.wallet,
    defaultPaymentMethodId: paymentMethodId,
  });
}

// HACK: smoke and mirrors b/c BE takes a sec to create the txn
export function createOrRenewSubscription(sub: UserSubscription) {
  const subExists = readState.wallet.subscriptions.some(
    ({ id }) => id === sub.id,
  );

  if (subExists) {
    return updateSubscription(sub);
  }

  const { paymentPlans, paymentProviders } = readState.wallet;

  const paymentMethod = getPaymentMethod({
    paymentProviders,
    providerType: sub.providerType,
    paymentMethodId: sub.paymentMethodId,
  });

  if (paymentMethod !== null) {
    if (isTrialSubscription(sub)) {
      devDebug("[wallet] Skipping transaction for trial subscription");
    } else {
      const { price, currency } = paymentPlans[sub.planName];

      const newTxn: UserTransaction = {
        currency,

        amountDue: price,
        amountPaid: price,
        tax: 0,

        createdAt: new Date(),

        isAttempted: false,
        isPaid: false,

        providerType: paymentMethod.providerType,

        paymentIntent: {
          ...(paymentMethod.type === "card"
            ? {
                paymentMethodCardBrand: paymentMethod.cardBrand,
                paymentMethodCardLast4: paymentMethod.cardLast4,
              }
            : {
                paymentMethodCardBrand: undefined,
                paymentMethodCardLast4: undefined,
              }),
        },

        items: [{ amount: price, currency, subscriptionId: sub.id }],

        paymentMethodId: paymentMethod.id,
        subscriptionId: sub.id,
      };

      const transactions = [...readState.wallet.transactions, newTxn];
      transactions[isPersistent] = true;
      writeState.wallet.transactions = transactions;
    }
  } else {
    devError(
      "Cannot add subscription transaction as the payment method used is missing.",
      sub,
    );

    sub.paymentMethodId = null;
  }

  const subscriptions = [sub, ...readState.wallet.subscriptions];
  subscriptions[isPersistent] = true;
  writeState.wallet.subscriptions = subscriptions;
}

export function updateSubscription(subUpdate: UserSubscription) {
  const subscriptions = [...readState.wallet.subscriptions];
  const index = subscriptions.findIndex(({ id }) => id === subUpdate.id);
  subscriptions.splice(index, 1, subUpdate);
  subscriptions[isPersistent] = true;
  writeState.wallet.subscriptions = subscriptions;
}

let initBraintreeClientTokenPromise: Promise<void> | null = null;

export async function ensureBraintreeClientToken() {
  const token = readState.volatile.braintreeClientToken;

  if (token) return;

  if (initBraintreeClientTokenPromise === null) {
    initBraintreeClientTokenPromise = (async () => {
      await initAuth();

      const bearerToken = await getBearerToken();

      if (bearerToken !== null) {
        const { braintreeClientToken } = await postData(
          createBraintreeClientToken(),
          CreateBraintreeClientTokenModel,
          undefined,
          {
            headers: {
              Authorization: await getBearerToken(),
            },
          },
        );

        setVolatileKV("braintreeClientToken", braintreeClientToken);
      }

      initBraintreeClientTokenPromise = null;
    })();
  }

  await initBraintreeClientTokenPromise;
}

export function clearBraintreeClientToken() {
  setVolatileKV("braintreeClientToken", null);
}

export function clearUserData() {
  removeWalletState();
  initWalletState();
  clearBraintreeClientToken();
}
